import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Leaf, Zap, Droplets, Sun, Shield } from 'lucide-react';

// Decorative blob component
const BlobBg = () => (
  <div className="absolute inset-0 -z-10 overflow-hidden">
    <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
    <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-secondary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent-sage/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
  </div>
);

// Floating decorative elements
const FloatingElements = () => (
  <>
    <motion.div 
      className="absolute top-1/4 left-1/4 w-12 h-12 rounded-full bg-primary-200/50 backdrop-blur-sm"
      animate={{
        y: [0, -30, 0],
        scale: [1, 1.1, 1],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
    <motion.div 
      className="absolute bottom-1/3 right-1/4 w-8 h-8 rounded-full bg-accent-jade/30 backdrop-blur-sm"
      animate={{
        y: [0, 30, 0],
        scale: [0.8, 1, 0.8],
      }}
      transition={{
        duration: 10,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 1
      }}
    />
  </>
);

// Feature card component
const FeatureCard = ({ icon: Icon, title, description, delay }) => (
  <motion.div
    className="p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 shadow-soft"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
    whileHover={{ y: -5, boxShadow: '0 10px 30px -10px rgba(16, 185, 129, 0.15)' }}
  >
    <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center mb-4 text-primary-600">
      <Icon className="w-6 h-6" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </motion.div>
);

const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 to-green-50 pt-20 pb-32">
      {/* Background elements */}
      <BlobBg />
      <FloatingElements />
      
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 z-0 opacity-10 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

      {/* Main content */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Hero text */}
          <motion.div
            className="text-left"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <motion.div
              className="inline-flex items-center px-4 py-2 rounded-full bg-primary-50 border border-primary-100 mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4, type: 'spring', stiffness: 200 }}
            >
              <Sparkles className="h-5 w-5 text-primary-600 mr-2" />
              <span className="text-primary-700 font-medium text-sm tracking-wide">GREEN LIVING STARTS HERE</span>
            </motion.div>

            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
            >
              Nurture Your <span className="text-primary-600">Green Haven</span>{' '}
              with Expert Plant Care
            </motion.h1>

            <motion.p
              className="text-lg text-gray-600 mb-8 max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8, ease: 'easeOut' }}
            >
              Transform your space into a thriving oasis with our expert plant care guides, personalized recommendations, and a community of passionate plant lovers.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: 'easeOut' }}
            >
              <motion.button
                onClick={() => scrollToSection('#plants')}
                className="group relative overflow-hidden bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center space-x-2 shadow-lg hover:shadow-primary-500/30 transform hover:scale-105"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10">Explore Our Collection</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300 relative z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.button>

              <motion.button
                onClick={() => scrollToSection('#guides')}
                className="group bg-white text-gray-800 hover:bg-gray-50 border-2 border-gray-200 hover:border-primary-200 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center space-x-2 shadow-sm hover:shadow transform hover:scale-105"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>Learn More</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
              </motion.button>
            </motion.div>

            {/* Features grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              <FeatureCard 
                icon={Zap} 
                title="Quick Tips" 
                description="Expert advice at your fingertips"
                delay={1.2}
              />
              <FeatureCard 
                icon={Droplets} 
                title="Watering Guide" 
                description="Never over or under water again"
                delay={1.4}
              />
              <FeatureCard 
                icon={Shield} 
                title="Plant Health" 
                description="Keep your plants thriving"
                delay={1.6}
              />
            </div>
          </motion.div>

          {/* Hero image */}
          <motion.div 
            className="relative mt-12 lg:mt-0"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
          >
            <div className="relative z-10 bg-white/80 backdrop-blur-sm p-4 rounded-3xl shadow-2xl border border-gray-100">
              <div className="relative overflow-hidden rounded-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1597848212624-a19eb35e2651?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80" 
                  alt="Lush indoor plants" 
                  className="w-full h-auto rounded-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent rounded-2xl"></div>
                
                {/* Decorative elements */}
                <motion.div 
                  className="absolute -top-4 -right-4 bg-white p-3 rounded-xl shadow-lg border border-gray-100"
                  animate={{ 
                    y: [0, -10, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ 
                    duration: 6, 
                    repeat: Infinity, 
                    ease: "easeInOut" 
                  }}
                >
                  <Leaf className="w-8 h-8 text-primary-600" />
                </motion.div>
                
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <h3 className="text-xl font-bold mb-1">Monstera Deliciosa</h3>
                  <p className="text-sm text-white/90">Thriving with your care</p>
                </div>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-primary-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 z-0"></div>
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-accent-sage/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 z-0"></div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        {/* <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.8 }}
        >
          <span className="text-sm text-gray-500 mb-2">Scroll to explore</span>
          <motion.div
            className="w-8 h-12 border-2 border-primary-200 rounded-full flex justify-center p-1"
            animate={{ 
              y: [0, 10, 0],
              borderColor: ['rgba(22, 163, 74, 0.2)', 'rgba(22, 163, 74, 0.5)', 'rgba(22, 163, 74, 0.2)']
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
          >
            <motion.div 
              className="w-1 h-3 bg-primary-500 rounded-full"
              animate={{ 
                y: [0, 8, 0],
                opacity: [0.6, 1, 0.6]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: 0.2
              }}
            />
          </motion.div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default Hero;
